/**
 * Feb 2026 — Unified customer display naming.
 *
 * Every list / dropdown / detail sheet across both the local and
 * export customer surfaces must render a customer the SAME way:
 *
 *   • Primary (bold)      → Business Name
 *   • Secondary (regular) → Contact Person
 *
 * Rules:
 *   1. If `business_name` is set, that is the primary label.
 *      The contact `name` is the secondary label BELOW it.
 *   2. If `business_name` is missing (walk-in / OTP signup where
 *      the customer didn't provide a company), the contact `name`
 *      is promoted to the primary label — there is no secondary.
 *   3. If both are set but IDENTICAL (case-insensitive trim), the
 *      contact line is suppressed — showing the same string twice
 *      just adds noise.
 *   4. If both are missing, fall back to `full_name` → `email` →
 *      `phone` → `—`.
 *
 * Callers should prefer the `<CustomerNameLabel>` component (see
 * `components/CustomerNameLabel.jsx`) — this module is exposed for
 * places that need the raw strings (option dropdowns, order
 * descriptions, share-link previews, etc.).
 */
const _norm = (v) => (v == null ? '' : String(v).trim());
const _same = (a, b) => _norm(a).toLowerCase() === _norm(b).toLowerCase();

/**
 * Return `{ primary, secondary }` for a customer record. `secondary`
 * is `null` when there is nothing meaningful to show below the
 * primary line.
 */
export function customerDisplayLabels(c) {
  const business = _norm(c?.business_name);
  const contact = _norm(c?.name || c?.full_name);
  const fallback = _norm(c?.email) || _norm(c?.phone) || '—';
  if (business && contact) {
    if (_same(business, contact)) return { primary: business, secondary: null };
    return { primary: business, secondary: contact };
  }
  if (business) return { primary: business, secondary: null };
  if (contact) return { primary: contact, secondary: null };
  return { primary: fallback, secondary: null };
}

/**
 * Single-line label — the primary + optional " · secondary" join.
 * Handy for option elements, page titles, order descriptions, etc.
 * where a two-line layout doesn't fit.
 */
export function customerSingleLine(c) {
  const { primary, secondary } = customerDisplayLabels(c);
  return secondary ? `${primary} · ${secondary}` : primary;
}

/** Bare primary label (Business Name if set, else Contact Name). */
export function customerPrimaryLabel(c) {
  return customerDisplayLabels(c).primary;
}
